import type { RdVerdict } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { SupplierVerdictChip } from './SupplierVerdictChip';

import type { SupplierWithBranches } from '../../lib/queries/suppliers';

// RECONCILED with the real Plan A codebase (real file wins):
//  * `SupplierWithBranches` is NOT a `@dcc-erp/shared` export (the plan named one
//    that does not exist) — it's the local entity+branches shape declared in
//    lib/queries/suppliers, whose branch rows live under `.vendors` (= Vendor
//    rows), NOT `.branches`.
//  * `rdVerdict` is a plain `string | null` snapshot on the entity; narrow it to
//    `RdVerdict | null` for the chip (the chip enumerates exactly that union).
//  * `@dcc-erp/ui` has no `edit` icon — the existing supplier modals use a valid
//    name; here `settings` is the edit affordance (UI-fidelity, no new icon).
export interface SupplierDetailProps {
  supplier: SupplierWithBranches;
  onAddBranch: () => void;
  onEditEntity: () => void;
  onEditBranch: (vendorId: string) => void;
}

export function SupplierDetail({
  supplier,
  onAddBranch,
  onEditEntity,
  onEditBranch,
}: SupplierDetailProps) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0 }}>{supplier.nameTh}</h3>
            <SupplierVerdictChip verdict={(supplier.rdVerdict as RdVerdict | null) ?? null} />
          </div>
          <div className="muted" style={{ marginTop: 6, display: 'flex', gap: 18, fontSize: 13 }}>
            <span>
              รหัส: <b>{supplier.code}</b>
            </span>
            <span>
              เลขภาษี: <b>{supplier.taxId ?? '—'}</b>
            </span>
            <span>
              terms: <b>{supplier.terms ?? '—'}</b>
            </span>
            <span>
              บัญชีเริ่มต้น: <b>{supplier.defaultExpenseCode ?? '—'}</b>
            </span>
          </div>
        </div>
        <button className="btn" onClick={onEditEntity}>
          <Icon name="settings" size={14} /> แก้ไขผู้ขาย
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 20,
          marginBottom: 8,
        }}
      >
        <div style={{ fontWeight: 600 }}>สาขา ({supplier.vendors.length})</div>
        <button className="btn primary" onClick={onAddBranch}>
          <Icon name="plus" size={14} /> เพิ่มสาขา
        </button>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>รหัสสาขา</th>
              <th>ชื่อสาขา</th>
              <th>ที่อยู่</th>
              <th>สถานะ</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {supplier.vendors.map((b) => (
              <tr key={b.id}>
                <td className="code">{b.branchCode}</td>
                <td>{b.branchName ?? '—'}</td>
                <td className="muted">{b.addressTh ?? '—'}</td>
                <td>
                  {b.isActive ? (
                    <span className="badge positive">
                      <span className="bdot" />
                      ใช้งาน
                    </span>
                  ) : (
                    <span className="badge neutral">
                      <span className="bdot" />
                      ปิดใช้งาน
                    </span>
                  )}
                </td>
                <td>
                  <button
                    className="btn ghost"
                    style={{ padding: '2px 8px', fontSize: 12 }}
                    onClick={() => onEditBranch(b.id)}
                  >
                    <Icon name="settings" size={12} /> แก้ไข
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
